import React from "react";
import A4PageFrame from "./A4PageFrame";
import ComparisonBarChart from "../../../components/charts/ComparisonBarChart";
import DonutChart from "../../../components/charts/DonutChart";
import { ROWS_PER_PAGE_LANDSCAPE, ROW_HEIGHT } from "./pageMetrics";
import { fmtNum, fmtDate } from "./formatters";

// Phase 3 (M2): shared landscape results list used by both "Résultats par objet"
// (grouped by module) and "Résultats par locataire" (grouped by tenant). Mirrors
// SurfacesList.jsx (landscape, fixed colgroup, ROWS_PER_PAGE pagination, colSpan
// subtotal + grand-total rows).
//
// 14 columns: Numéro · Type · Référence · Locataire · Genre d'objet · Surface ·
// Surface m² · FEE début · FEE fin · HT · CHF/m2 · TTC · Acomptes · Solde
//
// Props
//   data         { objects:[...], subtotals:[...], grand_total:{...} }
//   sectionTitle string  ("Résultats par objet" | "Résultats par locataire")
//   groupKey     "numero" | tenant grouping — determines the subtotal label source
//   subtotalLabel(sub) → string  (e.g. "Total <module>" or "Total <tenant>")
//   building, periodLabel, whiteLabelling, logoUrl, basePageNumber, grandTotalPages

// Plaines-6 feedback (latest): client asked for 1–2 more rows per page to fill
// the empty band at the bottom. Derived from the calibrated landscape page
// geometry (see pageMetrics.js) — 22 rows — instead of a hand-tuned 20. Shared
// single source of truth with the RESULTS_ROWS_PER_PAGE mirror in Preview.js.
const ROWS_PER_PAGE = ROWS_PER_PAGE_LANDSCAPE;

const ResultsList = ({
  data,
  sectionTitle,
  groupOf,
  subtotalKey,
  subtotalLabel,
  chartLabelOf,
  chartTitleSuffix,
  showCharts = true,
  building,
  periodLabel,
  periodYears,
  whiteLabelling,
  logoUrl,
  acomptesEnabled = true,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const yearLabel = periodYears?.current ?? "";
  const objects = data?.objects || [];
  const subtotals = data?.subtotals || [];
  const grand = data?.grand_total || {};

  const flatRows = buildFlatRows(objects, subtotals, grand, groupOf, subtotalKey);

  const pages = [];
  for (let i = 0; i < flatRows.length; i += ROWS_PER_PAGE) {
    pages.push(flatRows.slice(i, i + ROWS_PER_PAGE));
  }
  if (pages.length === 0) pages.push([]);

  // Optional charts page: HT per group bar + HT-share donut, both driven by
  // `subtotals`. Rendered only when the caller opts in (`showCharts` prop) AND
  // there are groups to chart. Résultats par locataire keeps it; Résultats par
  // objet opts out (showCharts={false}) to match the reference PDF, which has no
  // graph page for the objet table. Keep this gate in sync with the page-count
  // math in Preview.js.
  const labelOf = chartLabelOf || ((s) => String(s[subtotalKey] ?? ""));
  const barData = subtotals.map((s) => ({
    label: labelOf(s),
    current: Number(s.ht) || 0,
    prior: null,
  }));
  const donutData = subtotals
    .map((s) => ({ label: labelOf(s), value: Number(s.ht) || 0 }))
    .filter((d) => d.value > 0);
  const hasChartData = showCharts !== false && subtotals.length > 0;

  return (
    <>
      {pages.map((rows, pageIdx) => (
        <A4PageFrame
          key={pageIdx}
          building={building}
          periodLabel={periodLabel}
          sectionTitle={`${sectionTitle}${pages.length > 1 ? ` (${pageIdx + 1}/${pages.length})` : ""}`}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          pageNumber={basePageNumber + pageIdx}
          totalPages={grandTotalPages}
          landscape
          resultsPage
        >
          <table style={tbl}>
            {/* 15 columns; widths sum to ~1022px, well inside the ~1059px
                landscape content area (A4PageFrame padding 32px each side).
                Étang A feedback 2026-07-17: grand-total surface / HT / TTC
                were clipping ("106'788.3...", "5'810'157.87", "6'274'277.36").
                Widened numeric columns (Surface m² 56→72, HT 66→76, TTC 66→76)
                and reclaimed the 36 px from Locataire (112→96) and rue
                (112→92) which are already ellipsized. Total stays 1022 px. */}
            <colgroup>
              <col style={{ width: 42 }} />
              <col style={{ width: 78 }} />
              <col style={{ width: 92 }} />
              <col style={{ width: 96 }} />
              <col style={{ width: 92 }} />
              <col style={{ width: 78 }} />
              <col style={{ width: 50 }} />
              <col style={{ width: 56 }} />
              <col style={{ width: 72 }} />
              <col style={{ width: 56 }} />
              <col style={{ width: 76 }} />
              <col style={{ width: 46 }} />
              <col style={{ width: 76 }} />
              {/* Acomptes + Solde columns — hidden (and the table re-flows to fill
                  the freed width via tableLayout:fixed) when the per-period
                  Acomptes toggle is OFF. */}
              {acomptesEnabled && <col style={{ width: 56 }} />}
              {acomptesEnabled && <col style={{ width: 56 }} />}
            </colgroup>
            <thead>
              <tr style={{ borderBottom: "1px solid #00033F" }}>
                <th style={th}>Numéro</th>
                <th style={th}>Type</th>
                <th style={th}>Référence</th>
                <th style={th}>Locataire</th>
                <th style={th}>rue</th>
                <th style={th}>Genre d&apos;objet</th>
                <th style={thNum}>Surface</th>
                <th style={thNum}>Surface m²</th>
                <th style={thNum}>FEE début</th>
                <th style={thNum}>FEE fin</th>
                <th style={thNum}>{yearLabel} HT</th>
                <th style={thNum}>CHF/m2</th>
                <th style={thNum}>{yearLabel} TTC</th>
                {acomptesEnabled && <th style={thNum}>Acomptes</th>}
                {acomptesEnabled && <th style={thNum}>Solde</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                if (r.type === "subtotal" || r.type === "grand") {
                  const isGrand = r.type === "grand";
                  return (
                    <tr key={i} style={{ fontWeight: 700, borderTop: isGrand ? "2px solid #00033F" : "1px solid rgba(0,3,63,0.2)" }}>
                      <td style={td} colSpan={7}>{r.label}</td>
                      <td style={tdNum}>{fmtNum(r.surface_m2)}</td>
                      <td style={tdNum} />
                      <td style={tdNum} />
                      <td style={tdNum}>{fmtNum(r.ht)}</td>
                      <td style={tdNum} />
                      <td style={tdNum}>{fmtNum(r.ttc)}</td>
                      {acomptesEnabled && <td style={tdNum}>{fmtNum(r.acomptes)}</td>}
                      {acomptesEnabled && <td style={tdNum}>{fmtNum(r.solde)}</td>}
                    </tr>
                  );
                }
                const o = r.row;
                return (
                  <tr key={i}>
                    <td style={td}>{o.numero}</td>
                    <td style={td}>{o.type}</td>
                    {/* Référence = the canonical objet_code (e.g. 2689.01.0001.02),
                        matching the client reference PDF. Falls back to the raw
                        object reference when no objet_code was imported. */}
                    <td style={td}>{o.objet_code || o.reference}</td>
                    <td style={td}>{o.locataire || "—"}</td>
                    <td style={td}>{o.rue || ""}</td>
                    <td style={td}>{o.genre_objet || ""}</td>
                    <td style={tdNum}>{fmtNum(o.surface)}</td>
                    <td style={tdNum}>{fmtNum(o.surface_m2)}</td>
                    <td style={tdNum}>{fmtDate(o.fee_start)}</td>
                    <td style={tdNum}>{fmtDate(o.fee_end)}</td>
                    <td style={tdNum}>{fmtNum(o.ht)}</td>
                    <td style={tdNum}>{fmtNum(o.chf_m2)}</td>
                    <td style={tdNum}>{fmtNum(o.ttc)}</td>
                    {acomptesEnabled && <td style={tdNum}>{fmtNum(o.acomptes)}</td>}
                    {acomptesEnabled && <td style={tdNum}>{fmtNum(o.solde)}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </A4PageFrame>
      ))}

      {hasChartData && (
        <A4PageFrame
          building={building}
          periodLabel={periodLabel}
          sectionTitle={`${sectionTitle} — graphiques`}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          pageNumber={basePageNumber + pages.length}
          totalPages={grandTotalPages}
          landscape
          resultsPage
        >
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#00033F", marginBottom: 10 }}>
              Coûts HT par {chartTitleSuffix}
            </div>
            <ComparisonBarChart data={barData} width={720} labelWidth={180} />
          </div>
          <div style={{ marginTop: 32 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#00033F", marginBottom: 10 }}>
              Répartition par {chartTitleSuffix}
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <DonutChart data={donutData} width={320} />
            </div>
          </div>
        </A4PageFrame>
      )}
    </>
  );
};

// Flatten into rows in display order: each group's leases followed by its
// subtotal row, then a final grand-total row.
function buildFlatRows(objects, subtotals, grand, groupOf, subtotalKey) {
  const groups = new Map();
  for (const o of objects) {
    const g = groupOf(o);
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(o);
  }
  const subByGroup = new Map(subtotals.map((s) => [String(s[subtotalKey]), s]));

  const out = [];
  for (const [g, rows] of groups.entries()) {
    for (const o of rows) out.push({ type: "row", row: o });
    const s = subByGroup.get(String(g));
    if (s) {
      out.push({
        type: "subtotal",
        label: `Total ${s[subtotalKey === "module_code" ? "module_name" : "tenant_name"] || g}`,
        surface_m2: s.surface_m2,
        ht: s.ht,
        ttc: s.ttc,
        acomptes: s.acomptes,
        solde: s.solde,
      });
    }
  }
  out.push({
    type: "grand",
    label: "Total général",
    surface_m2: grand.surface_m2,
    ht: grand.ht,
    ttc: grand.ttc,
    acomptes: grand.acomptes,
    solde: grand.solde,
  });
  return out;
}

// 15 columns in landscape A4 → tighter padding + smaller font so everything
// fits and long values (locataire, rue) ellipsize instead of overflowing.
// marginBottom:0 overrides Bootstrap Reboot's `table{margin-bottom:1rem}` so the
// fixed content region keeps its full height (see pageMetrics.js).
const tbl = { width: "100%", borderCollapse: "collapse", marginTop: 4, marginBottom: 0, tableLayout: "fixed" };
const th = { padding: "3px 3px", fontWeight: 600, textAlign: "left", fontSize: 9, whiteSpace: "nowrap" };
const thNum = { ...th, textAlign: "right" };
const td = {
  // Row height PINNED to ROW_HEIGHT.landscape so subtotal/grand rows can't drift
  // taller and push the last row under the footer (recurring clipping bug).
  boxSizing: "border-box",
  height: ROW_HEIGHT.landscape,
  padding: "5px 3px",
  fontSize: 8.5,
  lineHeight: "14px",
  borderBottom: "1px solid rgba(0,3,63,0.05)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default ResultsList;
